import Immutable from 'seamless-immutable';
import _ from 'lodash';

import { TextTransformService } from 'console-kit';

import RefData from 'ref-data';

import { dateTimeService } from '../../common/Services';

const lockedStatus = {
  LOCKED:   'Locked',
  UNLOCKED: 'Unlocked',
};

/**
 * Campaign Promotion
 * @typedef {object} Campaign Promotion
 * @property {string} backgroundAssetRef
 * @property {string} badge
 * @property {string} byline
 * @property {string} campaignContainer
 * @property {string} caption
 * @property {RefData} classRef
 * @property {string} comments
 * @property {RefData} containerRef
 * @property {string} copy
 * @property {string} createdBy
 * @property {string} dateCreated
 * @property {RefData} detailStrategy
 * @property {RefData} displayStrategy
 * @property {string} expandedAssetRef
 * @property {string} expiryDate
 * @property {RefData} format
 * @property {string} fallbackAssetRef
 * @property {string} grantedMessage
 * @property {string} heading
 * @property {string} id
 * @property {string} img
 * @property {RefData} lastUpdated
 * @property {string} lockedMessage
 * @property {string} lockedThumbnailAssetRef
 * @property {RefData} mainAssetRef
 * @property {RefData} masterAssetRef
 * @property {RefData} modifiedBy
 * @property {string} name
 * @property {string} price
 * @property {string} pricePreText
 * @property {string} score
 * @property {string} source
 * @property {string} status
 * @property {string} target2Object
 * @property {string} target2Text
 * @property {string} targetObject
 * @property {string} targetText
 * @property {string} targetType
 * @property {string} thumbnailAssetRef
 * @property {RefData} typeRef
 * @property {string} unlockedMessage
 * @property {string} validFrom
 * @property {string} validTo
 *
 *
 */

function create(campaignPromotion) {
  const result = _.clone(campaignPromotion);

  result.typeRef = RefData.Model.hydrate(campaignPromotion.typeRef.id);
  result.displayStrategy = RefData.Model.hydrate(campaignPromotion.displayStrategy.id);
  result.detailStrategy = RefData.Model.hydrate(campaignPromotion.detailStrategy.id);
  result.classRef = RefData.Model.hydrate(campaignPromotion.classRef.id);
  result.format = RefData.Model.hydrate(campaignPromotion.format.id);

  // in order to provide empty line to the controlled inputs, when their value is null
  result.byline = campaignPromotion.byline || '';
  result.source = campaignPromotion.source || '';
  result.score = campaignPromotion.score || '';

  return Immutable(result, { prototype: model });
}

function deserialize(campaignPromotion) {
  const result = _.pick(campaignPromotion, _keys);
  return create(result);
}

function serialize(campaignPromotion) {
  const result = campaignPromotion.asMutable({ deep: true });

  result.typeRef = campaignPromotion.typeRef.id;
  result.displayStrategy = campaignPromotion.displayStrategy.id;
  result.detailStrategy = campaignPromotion.detailStrategy.id;
  result.classRef = campaignPromotion.classRef.id;
  result.format = campaignPromotion.format.id;

  return _.pick(result, _keys);
}

const timeZone = App.globalContext.userTimeZone.name; //TODO Remove dependency on global timezone
const dateNow = new Date().getTime();

const model = {
  get isUnlocked() {
    return this.isActive || this.isActiveOpen;
  },
  // NOTE: The sorting here MUST MATCH that in /src/main/groovy/com/ngx/common/optimisation/SortElementsUtils.groovy
  get isActive() {
    return (
      (this.validFrom && this.validTo && this.adjustedValidFrom < dateNow && dateNow < this.adjustedValidTo) ||
      (!this.validFrom && dateNow < this.adjustedValidTo) ||
      (!this.validTo && this.adjustedValidFrom < dateNow)
    );
  },
  get isActiveOpen() {
    return !this.validFrom && !this.validTo;
  },
  get isPast() {
    return this.validTo ? this.adjustedValidTo < dateNow : false;
  },
  get isFuture() {
    return this.validFrom ? this.adjustedValidFrom > dateNow : false;
  },
  get isInvalid() {
    return !this.isActive && !this.isActiveOpen && !this.isPast && !this.isFuture;
  },
  // EOF: NOTE
  get adjustedValidFrom() {
    return dateTimeService.toUserTimeZone(this.validFrom).getTime();
  },
  get adjustedValidTo() {
    return dateTimeService.toUserTimeZone(this.validTo).getTime();
  },
  get dateCreatedTimestamp() {
    return new Date(this.dateCreated).getTime();
  },
  get promotionHeader() {
    return this.isUnlocked ? this.heading || this.name : this.lockedMessage || this.name;
  },
  get promotionStatusLabel() {
    return this.isUnlocked ? lockedStatus.UNLOCKED : lockedStatus.LOCKED;
  },
  get promotionValidRange() {
    const _validRange = [];
    _validRange.push('Valid');

    if (this.validFrom)
      _validRange.push(`from ${TextTransformService.dateTime(this.validFrom, timeZone, { format: 'LL' })}`);
    if (this.validTo) _validRange.push(`to ${TextTransformService.dateTime(this.validTo, timeZone, { format: 'LL' })}`);
    return this.validFrom || this.validTo ? _validRange.join(' ') : null;
  },
  get allowEditLayout() {
    return (
      this.detailStrategy.id === RefData.Enum.STRATEGY.PROMOTION_DETAILS.PAGEBUILDER ||
      this.detailStrategy.id === RefData.Enum.STRATEGY.PROMOTION_DETAILS.INLINE
    );
  },
  get allowDelete() {
    return this.status.id !== RefData.Enum.STATUS.CAMPAIGNPROMOTION.PUBLISHED;
  },
  get infoTooltip() {
    const _tooltip = [];
    if (this.createdBy && !!this.createdBy.id) {
      _tooltip.push(`Created: ${TextTransformService.dateTime(this.dateCreated, timeZone, { format: 'LL' })}`);
      _tooltip.push(` by ${this.createdBy.id.split('@')[0]}`);
    }
    if (this.lastUpdated)
      _tooltip.push(`\nModified: ${TextTransformService.dateTime(this.lastUpdated, timeZone, { format: 'LL' })}`);
    if (this.modifiedBy && !!this.modifiedBy.id) _tooltip.push(` by ${this.modifiedBy.id.split('@')[0]}`);
    return _tooltip.join();
  },
};

const _keys = [
  'backgroundAssetRef',
  'badge',
  'byline',
  'campaignContainer',
  'caption',
  'classRef',
  'comments',
  'containerRef',
  'copy',
  'createdBy',
  'dateCreated',
  'detailStrategy',
  'displayStrategy',
  'expandedAssetRef',
  'expiryDate',
  'fallbackAssetRef',
  'format',
  'grantedMessage',
  'heading',
  'id',
  'img',
  'lastUpdated',
  'lockedMessage',
  'lockedThumbnailAssetRef',
  'mainAssetRef',
  'masterAssetRef',
  'modifiedBy',
  'name',
  'price',
  'pricePreText',
  'score',
  'source',
  'status',
  'target2Object',
  'target2Text',
  'targetObject',
  'targetText',
  'targetType',
  'thumbnailAssetRef',
  'typeRef',
  'unlockedMessage',
  'validFrom',
  'validTo',
  'thumbnailAssetRefAltText',
  'lockedThumbnailAssetRefAltText',
  'ctaLink',
  'ctaButtonText',
];

export default {
  create,
  deserialize,
  serialize,
};
